import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBulkDropdownData } from "../../api/keyvalue";
import { AlertCircle, CreditCard, Search } from "react-feather";
import { FORMS, MODULES } from "../../const";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Select } from "semantic-ui-react";
import Grid from "../../components/Grid";
import SearchDropdown from "../../components/input/SearchDropdown";
import { bankContains } from "../../api/bankApi";
import {
  ActiveCellStyle,
  StatusCellStyle,
  AsyncOtherRefDropdownValueRenderer,
} from "../../components/renderer/StyleRenderer";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import SideDrawer from "../../components/input/SideDrawer";
import Tab from "../../components/Tab";
import BankBranchAddComponent from "../../components/bankBranch/BankBranchAddComponent";
import {
  addBankBranchData,
  searchBankBranch,
  updateBankBranchData,
  getBankBranchDataById,
} from "../../api/bankBranchApi";
import { errorActions } from "../../store/error-slice";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";

const FORM = FORMS.BANK_BRANCH.BNK_BRN.form;

const BankBranch = () => {
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.BANK_BRANCH,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const [bank, setBank] = useState("");
  const [isBankSet, setIsBankSet] = useState(false);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const dispatch = useDispatch();
  const errorState = useSelector((state) => state.error.bankBranchErrors || {});
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [editBankBranch, setEditBankBranch] = useState({});
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdowns
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusOpt, setStatusOpt] = useState([]);
  const [activeOpt, setActiveOpt] = useState([]);

  const bankBranchRef = useRef();
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const bankCodeRef = useRef(null);

  const user = useSelector((state) => state.user);
  const [initialActive, setInitialActive] = useState(1);

  useEffect(() => {
    if (editMode && editBankBranch) {
      setInitialActive(editBankBranch.brnActive);
    }
  }, [editMode, editBankBranch]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { active, status } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
      ]);
      setActiveOptions(active || []);
      setActiveOpt(active || []);
      setStatusOptions(status || []);
      setStatusOpt(status || []);
      setIsLoading(false);

      // Perform initial search to show bank branch records on table immediately
      try {
        const response = await searchBankBranch({});
        const mappedResults = (response || []).map((item) => {
          const statusOption = (status || []).find(
            (o) =>
              Number(o.value) === Number(item.brnStatus) ||
              o.key === String(item.brnStatus)
          );
          const activeOption = (active || []).find(
            (o) =>
              Number(o.value) === Number(item.brnActive) ||
              o.key === String(item.brnActive)
          );
          const fallbackCode = `BRN${String(item.brnId || "").padStart(10, "0")}`;
          const code = item.brnCode && item.brnCode.trim() !== "" ? item.brnCode : fallbackCode;
          const bankCode = item.brnBankCode && item.brnBankCode.trim() !== "" ? item.brnBankCode : code;
          return {
            ...item,
            brnCode: code,
            brnBankCode: bankCode,
            statusText: statusOption ? statusOption.text : (Number(item.brnStatus) === 2 ? "Approved" : "Pending"),
            activeText: activeOption ? activeOption.text : (Number(item.brnActive) === 1 ? "Active" : "Inactive"),
          };
        });
        setSearchRes(mappedResults);
        setSearchClicked(true);
      } catch (err) {
        console.error("Error loading initial bank branches:", err);
      }
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditBankBranch(null);
    setDrawerTab(1);
    dispatch(errorActions.clearBankBranchError());
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    dispatch(errorActions.clearBankBranchError());
  };

  const reset = () => {
    bankBranchRef.current?.resetForm();
    setEditMode(false);
    setEditBankBranch(null);
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const data = bankBranchRef.current.getData();
    const isDeletedActive =
      Number(data.brnActive) === 2 && Number(initialActive) !== 2;

    if (!bypassDeleteCheck && isDeletedActive) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }

    setSubmitted(true);
    setIsSubmitLoading(true);
    dispatch(errorActions.clearBankBranchError());

    const validationErrors = bankBranchRef.current.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      dispatch(
        errorActions.validateBankBranch({
          bankBranchRequest: data,
        })
      );
      setIsSubmitLoading(false);
      toast.error("There was an error saving the Bank Branch details.", {
        autoClose: 5000,
        containerId: "Main",
      });
    } else {
      try {
        if (editMode) {
          await updateBankBranchData({
            ...data,
            lastModifiedBy: user.username,
            author: user.username,
          });
          toast.success("Bank Branch updated successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          await addBankBranchData({
            ...data,
            createdBy: user.username,
            author: user.username,
          });
          toast.success("Bank Branch added successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        handleDrawerClose();
        reset();
      } catch (err) {
        const errorMessage =
          (typeof err.response?.data === "string" && err.response.data) ||
          err.message ||
          "Something went wrong!";
        toast.error(errorMessage, {
          autoClose: 5000,
          containerId: "Main",
        });
      } finally {
        setIsSubmitLoading(false);
      }
    }
  };

  const handleSearchSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (tab === 1) {
      setIsLoading(false);
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }
    const response = await searchBankBranch({
      brnCode: codeRef.current ? codeRef.current.value : "",
      brnName: nameRef.current ? nameRef.current.value : "",
      brnBankCode: bankCodeRef.current ? bankCodeRef.current.value : "",
      brnBankReferance: bank || null,
      brnStatus: status,
      brnActive: active,
    });

    const mappedResults = (response || []).map((item) => {
      const statusOption = statusOptions.find(
        (o) =>
          Number(o.value) === Number(item.brnStatus) ||
          o.key === String(item.brnStatus)
      );
      const activeOption = activeOptions.find(
        (o) =>
          Number(o.value) === Number(item.brnActive) ||
          o.key === String(item.brnActive)
      );
      const fallbackCode = `BRN${String(item.brnId || "").padStart(10, "0")}`;
      const code = item.brnCode && item.brnCode.trim() !== "" ? item.brnCode : fallbackCode;
      const bankCode = item.brnBankCode && item.brnBankCode.trim() !== "" ? item.brnBankCode : code;
      return {
        ...item,
        brnCode: code,
        brnBankCode: bankCode,
        statusText: statusOption ? statusOption.text : "Pending",
        activeText: activeOption ? activeOption.text : "Active",
      };
    });
    setSearchRes(mappedResults);
    setSearchSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (codeRef.current) codeRef.current.value = "";
    if (nameRef.current) nameRef.current.value = "";
    if (bankCodeRef.current) bankCodeRef.current.value = "";
    setStatus(null);
    setActive(null);
    setBank("");
    setIsBankSet(false);
    setSearchFieldReset(true);
    setSearchRes([]);
    setSearchClicked(false);
    setResetVisible(false);
    setActiveAccordion1(true);
  };

  const deleteWarningModal = (
    <div style={{ padding: "20px" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AlertCircle color="red" size="25" />
        <p>Warning</p>
      </h3>
      <p>Active is set to Deleted. Are you sure you want to proceed?</p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button
          label="Cancel"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            setPendingSubmitEvent(null);
          }}
        />
        <Button
          label="Proceed"
          className="ui red button"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            handleSubmit(pendingSubmitEvent, true);
            setPendingSubmitEvent(null);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <CreditCard size={15} style={{ marginRight: "5px" }} />
            </span>
            Bank Branch Search
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            onClick={handleDrawerOpen}
            authenticated={false}
            editMode={false}
          />
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  handleSearchReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <h4 className="ui dividing header">Bank Branch Information</h4>
                <div className="four fields">
                  <div className="field">
                    <label>Branch Code</label>
                    <input
                      type="text"
                      placeholder="Branch Code"
                      name="brnCode"
                      ref={codeRef}
                    />
                  </div>

                  <div className="field">
                    <label>Branch Name</label>
                    <input
                      type="text"
                      placeholder="Branch Name"
                      name="brnName"
                      ref={nameRef}
                    />
                  </div>

                  <div className="field">
                    <label>Bank Code</label>
                    <input
                      type="text"
                      placeholder="Bank Code"
                      name="brnBankCode"
                      ref={bankCodeRef}
                    />
                  </div>

                  <SearchDropdown
                    key={1}
                    editMode={false}
                    reset={searchFieldReset}
                    setReset={setSearchFieldReset}
                    threshold={0}
                    clearable={true}
                    onResultSelect={(data) => {
                      setIsBankSet(true);
                      setBank(data);
                    }}
                    onClear={() => {
                      setBank("");
                      setIsBankSet(false);
                    }}
                    api={bankContains}
                    fieldInfo={{
                      tableName: "masterdata.tms_vm_banks",
                      primaryKey: "bnk_id",
                      fieldName: "bnk_name",
                      id: bank,
                    }}
                    label="Bank"
                  />
                </div>

                <div className="two fields">
                  <div className="field">
                    <label>Status</label>
                    <Select
                      clearable
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>Active</label>
                    <Select
                      clearable
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Branch Code",
                    field: "brnCode",
                    sort: "desc",
                    width: 120,
                  },
                  {
                    headerName: "Branch Name",
                    field: "brnName",
                  },
                  {
                    headerName: "Bank",
                    field: "brnBankReferance",
                    cellRenderer: AsyncOtherRefDropdownValueRenderer,
                    cellRendererParams: {
                      tableName: "masterdata.tms_vm_banks",
                      primaryKey: "bnk_id",
                      fieldName: "bnk_name",
                    },
                  },
                  {
                    headerName: "Bank Code",
                    field: "brnBankCode",
                    width: 120,
                  },
                  {
                    headerName: "Description",
                    field: "brnDescription",
                  },
                  {
                    headerName: "Status",
                    field: "statusText",
                    cellRenderer: StatusCellStyle,
                    width: 100,
                  },
                  {
                    headerName: "Active",
                    field: "activeText",
                    cellRenderer: ActiveCellStyle,
                    width: 100,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  dispatch(errorActions.clearBankBranchError());
                  try {
                    const response = await getBankBranchDataById(
                      props.data.brnId
                    );
                    setEditBankBranch(response || props.data);
                    setEditMode(true);
                    setIsDrawerOpen(true);
                  } catch (err) {
                    console.error("Error loading bank branch by ID", err);
                    setEditBankBranch(props.data);
                    setEditMode(true);
                    setIsDrawerOpen(true);
                  } finally {
                    setSearchFetchLoading(false);
                  }
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div
              className="bank-branch-header"
              style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
            >
              <h3>Bank Branch Code: {editBankBranch?.brnCode}</h3>
            </div>
          ) : null
        }
        body={
          <>
            <form className="drawer-content" onSubmit={handleSubmit}>
              <div style={{ width: "60vw", boxShadow: "none" }}>
                <div className="ui top attached tabular menu">
                  <Tab
                    Icon={CreditCard}
                    onClick={() => {
                      setDrawerTab(1);
                    }}
                    active={drawerTab === 1}
                    errors={
                      Object.keys(errorState.bankBranchErrors || {}).length !== 0
                    }
                    label="Bank Branch"
                    authenticated={true}
                    form={FORMS.BANK_BRANCH.BNK_BRN_DETAIL.form}
                    showMandatory={true}
                    isMandatory={true}
                  />
                </div>
                <div
                  className="ui bottom attached active tab segment"
                  style={{ display: drawerTab === 1 ? "block" : "none" }}
                >
                  <BankBranchAddComponent
                    ref={bankBranchRef}
                    isLoading={isLoading}
                    errors={errorState.bankBranchErrors}
                    dropdown={{
                      statusOpt,
                      activeOpt,
                    }}
                    editMode={editMode}
                    currentValues={editMode ? editBankBranch : null}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      authenticated={false}
                      editMode={editMode}
                    />
                  </div>
                </div>
              </div>
            </form>
            <Modal
              ref={deleteWarningRef}
              closeButton={false}
              minWidth={"300px"}
            >
              {showDeleteWarning && deleteWarningModal}
            </Modal>
          </>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default BankBranch;
